import React from 'react';
import { Link } from 'react-router-dom';
import { Music, DollarSign, Users, Clock, Star, CheckCircle, ArrowRight, Play } from 'lucide-react';
import Header from '../components/Header';

const BandDirectorsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-midnight">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="bg-midnight text-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-display">
              Kids Raise Money
              <br />
              <span className="text-goldie">By Playing Music</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-4xl mx-auto">
              Add your school band to the growing list of bands already earning money through personalized video messages. 
              <br />
              <span className="text-goldie font-bold">Free to join • Keep 85% of every sale</span>
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button className="bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold rounded-lg flex items-center justify-center">
                <Play className="w-5 h-5 mr-2" />
                Watch Success Stories
              </button>
              <Link to="/band-signup"
                className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black text-lg px-8 py-4 font-bold rounded-lg">
                Sign Up Your Band
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-goldie mb-2">$2,500+(EST)</div>
                <p className="text-gray-300">Average annual earnings per band</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-goldie mb-2">85%</div>
                <p className="text-gray-300">You keep of every video sold</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-goldie mb-2">$0</div>
                <p className="text-gray-300">Setup fees or monthly costs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Sign Up Free</h3>
              <p className="text-white-700">
                Complete a simple registration form. No fees, no commitments, no risk.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Record Videos</h3>
              <p className="text-white-700">
                Create personalized video messages when orders come in. Follow our simple guidelines for quality results.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-goldie w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4">Earn Money</h3>
              <p className="text-white-700">
                You set the price of each video. You keep 85% of every video sold.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center font-display">Why Band Directors Love Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <DollarSign className="w-8 h-8 text-goldie mr-3" />
                <h3 className="text-xl font-bold">Extra Revenue Stream</h3>
              </div>
              <p className="text-white-700">
                Generate additional income for your music program without any upfront investment. 
                Average bands earn $2,500+ annually.
              </p>
            </div>
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <Clock className="w-8 h-8 text-goldie mr-3" />
                <h3 className="text-xl font-bold">Flexible Schedule</h3>
              </div>
              <p className="text-white-700">
                Record videos on your own time. No strict deadlines or performance requirements. 
                Work around your existing schedule.
              </p>
            </div>
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <Users className="w-8 h-8 text-goldie mr-3" />
                <h3 className="text-xl font-bold">Community Connection</h3>
              </div>
              <p className="text-white-700">
                Strengthen ties with your school community while showcasing your students' musical talents.
              </p>
            </div>
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <Music className="w-8 h-8 text-goldie mr-3" />
                <h3 className="text-xl font-bold">Easy Recording</h3>
              </div>
              <p className="text-white-700">
                Using a smartphone or camera works perfectly. 
                We provide simple guidelines for quality results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Commission Details Section */}
      <section className="py-16 bg-goldie text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8 font-display">Keep 85% of Every Sale</h2>
          <div className="bg-white p-8 rounded-lg border-2 border-black mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left"><div>
            <h4 className="font-bold mb-2">Example Earnings:</h4>
            <ul className="text-gray-700 space-y-1">
              <li>• $50 video = $42.50 earnings</li>
              <li>• $75 video = $63.75 earnings</li>
              <li>• $100 video = $85.00 earnings</li>
            </ul>
              </div>
              <div>
                <h4 className="font-bold mb-2">Monthly Payouts:</h4>
                <ul className="text-gray-700 space-y-1">
                  <li>• Automatic payments</li>
                  <li>• No minimum threshold</li>
                  <li>• Direct deposit available</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-2">No Hidden Fees:</h4>
                <ul className="text-gray-700 space-y-1">
                  <li>• Free to join</li>
                  <li>• No monthly fees</li>
                  <li>• No setup costs</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-16 bg-midnight text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold mb-12 text-center font-display">Success Stories</h2>
        <p className="text-3xl font-bold mb-12 text-center font-display">(Placeholder for now)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
              </div>
              <p className="text-white-700 mb-4">
                "We've earned over $3,200 in just 2 events! The kids love being part of something 
                that brings joy to families, and the extra revenue has helped."
              </p>
              <p className="font-semibold">- Sarah Johnson, Band Director</p>
              <p className="text-sm text-white-600">Lincoln High School</p>
            </div>
            <div className="bg-sky text-black p-8 rounded-lg border-2 border-black">
              <div className="flex items-center mb-4">
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
                <Star className="w-5 h-5 text-goldie fill-current" />
              </div>
              <p className="text-white-700 mb-4">
                "The setup was incredibly easy. We use our existing equipment and the platform 
                handles all the customer service. It's been a game-changer for our program."
              </p>
              <p className="font-semibold">- Michael Chen, Music Director</p>
              <p className="text-sm text-white-600">Riverside Middle School</p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-sky text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center font-display">What You Need</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-goldie">Equipment Requirements</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Smartphone or camera and a low cost microphone. 
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Basic lighting (natural light is fine)
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Quiet space for recording
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Internet connection for uploads
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-goldie">Time Commitment</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  5-10 minutes per video
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Batch record when convenient for you
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  No minimum video requirements
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-goldie mr-3" />
                  Flexible scheduling
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-midnight text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">
            Ready to Start Earning?
          </h2>
            <p className="text-xl mb-8 text-gray-300">
              Join bands already making money with BandCam.io.<br />
              Sign up today and get started.
            </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                         <Link
               to="/band-signup"
               className="bg-goldie text-black hover:bg-opacity-90 text-lg px-8 py-4 border-2 border-black font-bold rounded-lg flex items-center justify-center"
             >
               Sign Up Your Band
               <ArrowRight className="ml-2 w-5 h-5" />
             </Link>
            <button className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black text-lg px-8 py-4 font-bold rounded-lg">
              Schedule a Call
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-4">
            No commitment • Free to join • Start earning immediately
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-midnight text-white border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="BandCam.io Logo" 
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-2 text-xl font-bold">BandCam.io</span>
              </div>
              <p className="text-gray-300 mb-4">
                Connecting families with their school bands to create unforgettable personalized video messages for special occasions.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/schools" className="text-gray-300 hover:text-white">Find Your School</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/help" className="text-gray-300 hover:text-white">Help Center</Link></li>
                <li><Link to="/faq" className="text-gray-300 hover:text-white">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
            <p>&copy; {new Date().getFullYear()} BandCam.io. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BandDirectorsPage;
